/**
 * Backbone view for a form that can create or modify a project
 * When instantiating this view an 'el' must be passed in e.g. new ProjectFormView({el: '.parent'})
 * If the form doesn't already exist inside of the 'el', the form will be rendered client side and injected via .html().
 *
 * The project form is a multipart (as projects can be created with attached photos) so this view uses the form_file_upload.js module.
 *
 * Public Methods:
 * loadProject(projectId) - project will be fetched via ajax and loaded into form
 * reset() - destroys the form and backbone model and creates a new one
 *
 * Published Events
 * Once a project is successfully saved a global backbone event will be triggered (via form_file_upload.js)
 * Backbone.trigger('project:saved', projectData)
 */
const Backbone = require('backbone');
const ProjectModel = require('app/models/store/client/projects/project');
const FormValidator = require('formValidator');
const FormFileUploadView = require('common/form_file_upload');
const dustjs = require('dustjs');
const _projectFormDust = require('common/_project_form.dust');
// putting some strings up here for easy reference
var _formSelector = 'form.js-project-form',
	_loadFormErrorMsg = 'Sorry there was a problem building the form!',
	_loadProjectErrorMsg = "Sorry! We coudn't fetch your project.";

var ProjectFormView = Backbone.View.extend({
	/**
	 * 'el' must be passed in options, as this module is indifferent to where it get's rendered.
	 */
	el: '',

	initialize: function() {
		// if model isn't passed in via options (gets auto attached to view) then create an empty
		// model so the form is ready to post.
		if (!this.model) {
			this.model = new ProjectModel();
		}

		// if container doesn't already contain the project form render it client side
		if (!this.$(_formSelector).length) {
			this._renderForm(this.$el);
		} else {
			this.$projectForm = this.$(_formSelector);
			this._bindFormToThings();
		}
	},

	/**
	 * Public. Will fetch and load a project into the form for editing.
	 * @param  {number}   projectId
	 * @param  {Function} callback
	 * @return {Object} promise
	 */
	loadProject: function(projectId, callback) {
		var self = this;
		this._fetchProject(projectId, function() {
			self._renderForm(self.$el);
			if (typeof callback === 'function') {
				callback();
			}
		});
	},

	/**
	 * Public. Will fetch, set archived property and save.
	 * @param  {number}   projectId
	 * @param  {boolean}  isArchive
	 * @param  {Function} callback
	 */
	toggleProjectArchive: function(projectId, isArchive, callback) {
		var self = this;
		this._fetchProject(projectId, function() {
			self.model.set('archived', Boolean(isArchive));
			self.model
				.save()
				.done(callback)
				.fail(callback);
		});
	},

	/**
	 * Will clear out the project form and model and re-render an empty form
	 */
	reset: function() {
		this.model = new ProjectModel();
		this.$el.empty();
		this._renderForm(this.$el);
	},

	/**
	 * Fetches a project from server
	 * @param  {number}   projectId
	 * @param  {Function} callback
	 */
	_fetchProject: function(projectId, callback) {
		this.model.set('id', projectId);
		return this.model
			.fetch()
			.done(function() {
				if (typeof callback === 'function') {
					callback();
				}
			})
			.fail(function() {
				window.BCOM.notifications.render(_loadProjectErrorMsg, 'danger');
			});
	},

	/**
	 * Set the form validator for this view
	 */
	_bindFormToThings: function() {
		this.validator = FormValidator.init({
			el: this.$projectForm
		});

		// FormFileUploadView handles ajax submitting forms with file attachment
		this.projectPostFormView = new FormFileUploadView({
			el: this.$projectForm[0],
			sanitize: ['textarea[name=description]', 'input[name=name]'],
			eventName: 'project:saved',
			model: this.model,
			onFail: this._duplicateProjectNameError
		});
	},

	/**
	 * Will render the _project_form.dust on client side.
	 * This is auto triggered if options.container is passed into View instantiation.
	 * @param  {Object} $container Pass in element in which form will be rendered.
	 */
	_renderForm: function($container) {
		var self = this,
			data = Object.assign(this.model.toJSON(), {
				isBuild: window.BCOM.theme.isBuild
			});
		dustjs.render(_projectFormDust, data, function(err, html) {
			if (err) {
				$container.html(_loadFormErrorMsg);
				return;
			}
			$container.html(html);
			self.$projectForm = $container.find(_formSelector);
			self._bindFormToThings();
		});
	},
	_duplicateProjectNameError(response) {
		const $projectElem = $(_formSelector)
			.find('#project-name')
			.closest('.form-group');
		$projectElem.addClass('has-error');
		$projectElem.append(`<label class="error">${response.error}</label>`);
	}
});

module.exports = ProjectFormView;
